import _ from 'lodash';
import { useEffect, ReactNode, useMemo } from 'react';
import { BaseIcon, Button, OptionsMenu, Tag, Tooltip } from '@scale/scaleui';
import Link from 'next/link';
import { ViewTitle } from './ViewTitle';
import { twMerge } from 'tailwind-merge';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import { useFilters } from 'react-scale/corp/views/CorpProjectGroupManager/components/FiltersProvider';
import { GenAIOpsHubFilters } from 'react-scale/corp/views/CorpProjectGroupManager/GenAIOpsHub';
import { getViewMenuItems } from 'react-scale/corp/views/CorpProjectGroupManager/components/layout';
import { useQMProject } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

const BASE_TITLE = 'GenAI Ops Hub';

type ViewHeaderProps = {
  className?: string;
  title: string;
  titleClassName?: string;
  titleTextClassName?: string;
  internalUrl?: string;
  internalLabel?: string;
  externalUrl?: string;
  externalUrlLabel?: string;
  children?: ReactNode;
  menuItemsName?: string;
  customTitle?: ReactNode;
};

const ViewHeader: React.FC<ViewHeaderProps> = ({
  className,
  titleClassName,
  titleTextClassName,
  title,
  children,
  externalUrl,
  externalUrlLabel = '',
  internalUrl,
  internalLabel = '',
  menuItemsName,
  customTitle,
}: ViewHeaderProps) => {
  const url = window !== undefined ? window.location.origin : '';
  const projectId = useProjectId();

  const { data: project, isLoading: projectLoading } = useQMProject(projectId);

  const { dateRange } = useFilters(s => ({
    dateRange: (s.filters as GenAIOpsHubFilters).dateRange,
  }));

  const menuItems = useMemo(() => {
    const view = menuItemsName || title;
    return getViewMenuItems({ view, projectId, dateRange });
  }, [menuItemsName, title, projectId, dateRange]);

  const fullExternalUrl = externalUrl
    ? `${!externalUrl.startsWith('http') ? url : ''}${externalUrl}`
    : null;
  const fullInternalUrl = internalUrl
    ? `${!internalUrl.startsWith('http') ? url : ''}${internalUrl}`
    : null;

  useEffect(() => {
    document.title = `${BASE_TITLE} - ${_.startCase(title)}`;
  }, [title]);

  const isTestSandbox = !projectLoading && project?.isSandbox;

  return (
    <div
      className={twMerge(
        'px-6 pt-3 bg-neutral-0 border-b shadow-sm border-b-neutral-200 space-y-3',
        !children && 'pb-3',
        className,
      )}
    >
      <div className={twMerge('flex items-center gap-4', titleClassName)}>
        <ViewTitle className={twMerge('mr-auto', titleTextClassName)}>{title}</ViewTitle>
        {isTestSandbox ? (
          <div className="rounded-full flex items-center justify-center m-2 gap-2 shrink-0">
            <Tag size="lg" variant="warning" icon={['far', 'fence']}>
              Test Sandbox Project
              <Tooltip
                text="You're currently viewing a test sandbox project, only tests users can access this project."
                target={
                  <div className="flex gap-1">
                    <BaseIcon
                      size="sm"
                      className="text-neutral-600"
                      icon={['far', 'question-circle']}
                      variant="warning"
                    />
                  </div>
                }
                asChild={true}
              />
            </Tag>
          </div>
        ) : null}
        {menuItems.length > 0 && (
          <OptionsMenu
            buttonIcon="ellipsis-h"
            className=""
            direction="bottom-right"
            icon="ellipsis-h"
            iconSize="sm"
            iconVariant="neutral"
            menuItems={menuItems}
            size="lg"
            variant="neutral"
          />
        )}
        {fullExternalUrl ? (
          <Link
            href={fullExternalUrl}
            className="scaleui scale-theme scale-tailwind flex gap-1 items-center text-primary-400 "
            target="_blank"
          >
            {externalUrlLabel}
            <BaseIcon
              icon={['fas', 'external-link-alt']}
              contrastText
              edge="end"
              variant="secondary"
            />
          </Link>
        ) : null}
        {fullInternalUrl ? (
          <Link
            href={fullInternalUrl}
            /* TODO: Delete this target="_blank" when we move to fully use NextJS instead of React Router Dom */
            target="_blank"
          >
            <Button variant="primary" size="lg">
              {internalLabel}
            </Button>
          </Link>
        ) : null}
        {customTitle}
      </div>
      {children}
    </div>
  );
};

export { ViewHeader };
